import React from "react";
import {
  SEO,
  PageLayout,
  Section,
  AnimatedSelector,
  BackgroundImages,
  FishermanHeader,
  MarkdownContent,
  WebsiteComponentInteractions,
  OutboundLink,
  MultipleComponentIterator,
  SlideReveal,
  Conditional,
  Location,
  Phone,
  Email,
  HoursOfOperation,
  InternalLink,
  Image,
  FadeReveal,
  BasicCarousel,
  componentIterator,
} from "@bluefin/components";
import { Button, Grid, Card } from "semantic-ui-react";
import { graphql } from "gatsby";

import WebsiteLayout from "../components/WebsiteLayout.jsx";
import {
  getComponentFiles,
  getComponentContentNodeContent,
  getComponentInteractions,
} from "../utils/utils";
import { createLocationSlug } from "@bluefin/gatsby-plugin-fisherman-page-creator";

export default class LocationsPage extends React.PureComponent {
  render() {
    const { data } = this.props;
    const {
      fishermanBusiness,
      fishermanBusinessWebsitePage,
      bookingUrl,
      allFishermanBusinessLocation,
    } = data;

    return (
      <WebsiteLayout>
        <SEO
          businessName={fishermanBusiness.name}
          pageName={fishermanBusinessWebsitePage.title}
          description={fishermanBusinessWebsitePage.description}
          images={getComponentFiles({
            components: fishermanBusinessWebsitePage.components,
            componentName: "HeroElement",
            numToSelect: 1,
          })}
          title={fishermanBusinessWebsitePage.seoTitle}
          ogUrl={""}
        />
        <PageLayout className={"locations-page"} hero={null}>
          <Section className={"default-hero-section"}>
            <AnimatedSelector
              targetSelector={
                ".image.background::before, .image.background::after"
              }
              duration={1500}
              transitionType={"zoom"}
              transitionProps={{
                animating: { scale: 1 },
                preAnimating: { scale: 1.4 },
                serverSide: { scale: 1.4 },
              }}
              easing={"cubic-bezier(0,1,0.99,1)"}
              runPolicy={"RUN_ONCE_EVERY_VISIT"}
              animationKey={"locations-hero-animation"}
            >
              <BackgroundImages
                images={getComponentFiles({
                  components: fishermanBusinessWebsitePage.components,
                  componentName: "HeroElement",
                })}
                carouselProps={{
                  leftArrowIcon: "arrow-left",
                  rightArrowIcon: "arrow-right",
                }}
                imageProps={{ loadStrategy: "eager" }}
              >
                <div className={"content-container"}>
                  <FishermanHeader
                    as={"h1"}
                    content={getComponentContentNodeContent({
                      components: fishermanBusinessWebsitePage.components,
                      componentId: "locations_hero_header",
                      defaultValue: "Our Locations",
                    })}
                  />
                  <MarkdownContent
                    content={getComponentContentNodeContent({
                      components: fishermanBusinessWebsitePage.components,
                      componentId: "locations_hero_description",
                    })}
                  />
                  <WebsiteComponentInteractions
                    alignment={"center"}
                    ctas={getComponentInteractions({
                      components: fishermanBusinessWebsitePage.components,
                      componentId: "locations_hero_description",
                    })}
                    fallback={
                      <React.Fragment>
                        <Button
                          primary={true}
                          as={OutboundLink}
                          to={bookingUrl.url}
                          event={{
                            category: "Booking",
                            action: "Schedule Appointment Intent",
                          }}
                          content={bookingUrl.title}
                        />
                      </React.Fragment>
                    }
                  />
                </div>
              </BackgroundImages>
            </AnimatedSelector>
          </Section>
          <Section className={"location-cards-section"}>
            <Grid stackable={true}>
              <Grid.Column width={10} className={"cards"}>
                <Card.Group itemsPerRow={2}>
                  <MultipleComponentIterator
                    iterator={allFishermanBusinessLocation.nodes}
                    components={[
                      {
                        component: (
                          <SlideReveal
                            className={"ui card"}
                            direction={"up"}
                            triggerOnce={true}
                            fade={true}
                          />
                        ),
                        propMap: { key: "_id" },
                        children: [
                          {
                            component: <Card />,
                            children: [
                              {
                                component: <Card.Content />,
                                children: [
                                  {
                                    component: <FishermanHeader />,
                                    children: [
                                      {
                                        component: <Conditional />,
                                        children: [
                                          {
                                            component: (
                                              <Conditional.Condition />
                                            ),
                                            propMap: {
                                              condition: "name",
                                              children: "name",
                                            },
                                          },
                                          {
                                            component: (
                                              <Conditional.Condition />
                                            ),
                                            propMap: {
                                              condition: "city",
                                              children: "city",
                                            },
                                          },
                                          {
                                            component: <Conditional.Default />,
                                            propMap: { children: "street" },
                                          },
                                        ],
                                      },
                                    ],
                                  },
                                  {
                                    component: <MarkdownContent />,
                                    propMap: { content: "description" },
                                  },
                                  {
                                    component: (
                                      <div className={"location-details"} />
                                    ),
                                    children: [
                                      {
                                        component: <Location inline={true} />,
                                        propMap: {
                                          "address.street": "street",
                                          "address.city": "city",
                                          "address.state": "state",
                                          "address.zip": "zipCode",
                                        },
                                      },
                                      {
                                        component: <Phone />,
                                        propMap: { phone: "phoneNumber" },
                                      },
                                      {
                                        component: <Email />,
                                        propMap: { email: "email" },
                                      },
                                      {
                                        component: (
                                          <HoursOfOperation
                                            displayOption={"dailyWithPopup"}
                                            displayOpenClosedStatus={true}
                                            enhancedLabels={{
                                              openingSoon: "Opening soon",
                                              open: "Open now",
                                            }}
                                          />
                                        ),
                                        propMap: {
                                          hours: "hours",
                                          timezone: "timezone",
                                        },
                                      },
                                    ],
                                  },
                                  {
                                    component: (
                                      <div className={"ctas-container"} />
                                    ),
                                    children: [
                                      {
                                        component: (
                                          <Button
                                            primary={true}
                                            className={"location-details"}
                                            content={"More Details"}
                                            as={InternalLink}
                                            event={{
                                              category: "Locations",
                                              action: "View Location Details",
                                            }}
                                          />
                                        ),
                                        propMap: {
                                          key: "_id",
                                          "event.label": "name",
                                          to: createLocationSlug,
                                        },
                                      },
                                    ],
                                  },
                                ],
                              },
                            ],
                          },
                        ],
                      },
                    ]}
                  />
                </Card.Group>
              </Grid.Column>
              <Grid.Column width={6} className={"content"}>
                <FishermanHeader
                  as={"h2"}
                  className={"highlighted"}
                  content={getComponentContentNodeContent({
                    components: fishermanBusinessWebsitePage.components,
                    componentId: "locations_section_highlight_header",
                    defaultValue: "Top salon and beauty services",
                  })}
                />
              </Grid.Column>
            </Grid>
          </Section>
          <Section className={"book-now-section"}>
            <Image
              background={true}
              src={getComponentFiles({
                components: fishermanBusinessWebsitePage.components,
                componentIdentifier: "book_now_section_background_image",
                numToSelect: 1,
              })}
            >
              <FishermanHeader
                as={"h2"}
                content={getComponentContentNodeContent({
                  components: fishermanBusinessWebsitePage.components,
                  componentId: "book_now_section_header",
                  defaultValue: "Not sure what to book?",
                })}
              />
              <MarkdownContent
                content={getComponentContentNodeContent({
                  components: fishermanBusinessWebsitePage.components,
                  componentId: "book_now_section_description",
                  defaultValue: "",
                })}
              />
              <WebsiteComponentInteractions
                alignment={"center"}
                ctas={getComponentInteractions({
                  components: fishermanBusinessWebsitePage.components,
                  componentId: "book_now_section_description",
                })}
                fallback={
                  <React.Fragment>
                    <Button
                      primary={true}
                      as={OutboundLink}
                      to={bookingUrl.url}
                      event={{
                        category: "Booking",
                        action: "Schedule Appointment Intent",
                      }}
                      content={bookingUrl.title}
                    />
                  </React.Fragment>
                }
              />
            </Image>
          </Section>
          <Section className={"services-section"}>
            <FishermanHeader
              as={"h2"}
              content={getComponentContentNodeContent({
                components: fishermanBusinessWebsitePage.components,
                componentId: "services_section_header",
                defaultValue: "We'll treat your hair the way we treat ours.",
              })}
            />
            <Grid stackable={true}>
              <Grid.Column width={8} className={"images"}>
                <FadeReveal duration={1250} triggerOnce={true} delay={0}>
                  <BasicCarousel
                    size={1}
                    slide={true}
                    displayIndicators={false}
                    displayPlayPauseButton={true}
                    autoScroll={false}
                    leftArrowIcon={"arrow-left"}
                    rightArrowIcon={"arrow-right"}
                    mobile={{ size: 1 }}
                    items={componentIterator({
                      iterator: getComponentFiles({
                        components: fishermanBusinessWebsitePage.components,
                        componentIdentifier: "services_section_images",
                      }),
                      component: <Image background={true} />,
                      propMap: { src: "__all__" },
                    })}
                  />
                </FadeReveal>
              </Grid.Column>
              <Grid.Column width={8} className={"content"}>
                <FadeReveal duration={1250} triggerOnce={true} delay={450}>
                  <div>
                    <MarkdownContent
                      content={getComponentContentNodeContent({
                        components: fishermanBusinessWebsitePage.components,
                        componentId: "services_section_description",
                      })}
                    />
                    <WebsiteComponentInteractions
                      ctas={getComponentInteractions({
                        components: fishermanBusinessWebsitePage.components,
                        componentId: "services_section_description",
                      })}
                      fallback={
                        <React.Fragment>
                          <Button
                            primary={true}
                            event={{
                              category: "Services",
                              action: "View All Services",
                            }}
                            as={InternalLink}
                            to={"/services/"}
                          >
                            <span
                              children={getComponentContentNodeContent({
                                components:
                                  fishermanBusinessWebsitePage.components,
                                componentId:
                                  "services_section_see_all_services_button",
                                defaultValue: "Our Services",
                              })}
                            />
                          </Button>
                        </React.Fragment>
                      }
                    />
                  </div>
                </FadeReveal>
              </Grid.Column>
            </Grid>
          </Section>
        </PageLayout>
      </WebsiteLayout>
    );
  }
}

export const query = graphql`
  query {
    fishermanBusiness {
      name
    }
    fishermanBusinessWebsitePage(pageType: { eq: "Locations" }) {
      title
      description
      components {
        fastId
        componentIdentifier
        order
        files {
          file
          altText
          gatsbyFile {
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
        }
        contentNodes {
          content
        }
        interactions {
          _id
          order
          behaviorType
          displayType
          enabled
          label
          style
          url
          role
        }
      }
      seoTitle
    }
    bookingUrl: fishermanBusinessWebsitePage(
      title: { regex: "/Book Now|Make An Appointment/" }
      pageType: { eq: "External" }
    ) {
      url
      title
    }
    allFishermanBusinessLocation {
      nodes {
        _id
        street
        city
        state
        zipCode
        name
        description
        phoneNumber
        email
        hours {
          open
          close
          day
          label
        }
        timezone
        interactions {
          _id
          behaviorType
          displayType
          enabled
          label
          style
          url
          role
        }
      }
    }
  }
`;
